"use client";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Database } from "../types/supabase";
import { createClient, createLegacyClient } from "../utils/supabase/client";

type SupabaseContextValue = {
  supabase: SupabaseClient<Database>;
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  syncSession: () => Promise<Session | null>;
};

const SupabaseContext = createContext<SupabaseContextValue | null>(null);

export function SupabaseProvider({ children }: { children: React.ReactNode }) {
  const supabase = useMemo(() => {
    if (typeof window === "undefined") {
      return createClient();
    }
    let embedded = false;
    try {
      embedded = window.self !== window.top;
    } catch {
      embedded = true;
    }
    return embedded ? createLegacyClient() : createClient();
  }, []);

  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setIsLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
      },
    );
    return () => sub.subscription.unsubscribe();
  }, [supabase]);

  // Re-reads the session from storage. Another window signing in normally
  // reaches `onAuthStateChange` above through supabase-js's BroadcastChannel;
  // this is the fallback for when it doesn't (no BroadcastChannel, a frozen
  // tab), e.g. once the signup dialog's Google popup reports back.
  const syncSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const next = data.session ?? null;
    setSession((prev) =>
      prev?.access_token === next?.access_token ? prev : next,
    );
    return next;
  }, [supabase]);

  const value = useMemo(
    () => ({
      supabase,
      supabaseClient: supabase,
      session,
      user: session?.user ?? null,
      isLoading,
      syncSession,
    }),
    [supabase, session, isLoading, syncSession],
  );

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
}

export function useSupabaseClient() {
  const ctx = useContext(SupabaseContext);
  if (!ctx) throw new Error("SupabaseProvider missing");
  return ctx.supabase;
}

export function useSession() {
  const ctx = useContext(SupabaseContext);
  if (!ctx) throw new Error("SupabaseProvider missing");
  return ctx.session;
}

export function useUser() {
  const ctx = useContext(SupabaseContext);
  if (!ctx) throw new Error("SupabaseProvider missing");
  return ctx.user;
}

export function useSessionContext() {
  const ctx = useContext(SupabaseContext);
  if (!ctx) throw new Error("SupabaseProvider missing");
  return ctx;
}
